import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/Actions";
import CustomButton from "../../components/CustomButton";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import CustomSelect from "../../components/CustomSelect";
import CustomTable from "../../components/table/CustomTable";
import CustomTextField from "../../components/CustomTextField";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import ConfirmDialog from "../../components/ConfirmDialog";
import SettingToggleRow, {
  SettingsCard,
} from "../../components/settings/SettingsCard";
import ServiceScopePickerDialog, {
  ServiceCatalogueEntry,
} from "../../components/loyalty/ServiceScopePickerDialog";
import {
  addLoyaltyPoints,
  createReward,
  BusinessShareSettingPayload,
  FIXED_SHARE_DEFAULT_DIVISOR,
  FIXED_SHARE_MAX_DIVISOR,
  FIXED_SHARE_MIN_DIVISOR,
  getRewardServiceIds,
  getRewardServiceScope,
  LoyaltyServiceScope,
  normalizeBusinessShareSettings,
  saveBusinessShareSettings,
  deleteReward,
  getRewards,
  getLoyaltySettings,
  getLoyaltyTransactions,
  getLoyaltyUsersPage,
  initializeExistingLoyaltyUsers,
  LoyaltyEarnMode,
  LoyaltyReward,
  LoyaltyRewardType,
  LoyaltyUsersFilter,
  removeLoyaltyPoints,
  saveLoyaltySettings,
  setRewardActive,
  setRewardPin,
  transferLoyaltyPoints,
  updateReward,
  updateFixedShareSettings,
  updateFullInvoiceEnabled,
  updateLoyaltyEnabled,
  updateSharePointsEnabled,
} from "../../services/LoyaltyService";
import { fetchProducts } from "../../services/ProductsNewService";
import { getAllBranches } from "../../services/BranchesService";
import { getAllServicesByBusiness } from "../../services/ServicesService";
import { getAllBusinesses } from "../../services/VenueService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useRemoteSearchOptions } from "../../hooks/useRemoteSearchOptions";

type LoyaltyUserRow = {
  id: string;
  name: string;
  mobile?: string;
  email?: string;
  points: number;
};

const LOYALTY_USERS_PAGE_SIZE = 30;

const toLoyaltyUserRow = (user: any): LoyaltyUserRow => {
  const userId = String(user.id || user.coreUserId || "");
  return {
    id: userId,
    name: user.name || user.full_name || `User ${userId}`,
    mobile: user.mobile || user.phone || "",
    email: user.email || "",
    points: Number(user.points ?? user.balance ?? 0),
  };
};

type LoyaltyTransactionRow = {
  id: string | number;
  type: string;
  points: number;
  paymentId?: string | null;
  createdAt?: string | null;
};

type RedeemDiscountType = "PERCENT" | "FIXED";

type RewardItemOption = {
  label: string;
  value: string;
};

type BusinessOption = {
  id: string;
  name: string;
};

// A plain typable field: type="number" left only the spinner arrows working,
// so digits are kept out by hand instead.
const sanitizeDivisorInput = (value: string) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 2);

const DIVISOR_INPUT_PROPS = {
  inputMode: "numeric" as const,
  pattern: "[0-9]*",
  maxLength: 2,
};

const divisorRangeMessage = (blankInherits = false) =>
  `Players per match must be a whole number between ${FIXED_SHARE_MIN_DIVISOR} and ${FIXED_SHARE_MAX_DIVISOR}${
    blankInherits ? ", or blank to inherit" : ""
  }.`;

// A divisor input mid-edit ("1" on the way to "12") is not a divisor yet.
const readDivisorInput = (value: string) => {
  const numeric = Number(value);
  return value !== "" &&
    Number.isInteger(numeric) &&
    numeric >= FIXED_SHARE_MIN_DIVISOR &&
    numeric <= FIXED_SHARE_MAX_DIVISOR
    ? numeric
    : null;
};

/**
 * The most players a session's points can be split between: the largest of
 * the org number and every business's own, with a blank business inheriting
 * the org's.
 */
const resolveSessionSplitDivisor = (
  orgDivisor: number,
  businessDivisors: string[],
) =>
  businessDivisors.reduce(
    (max, divisor) => Math.max(max, readDivisorInput(divisor) ?? orgDivisor),
    orgDivisor,
  );

const sanitizePointsPerSessionInput = (value: string) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 6);

const POINTS_PER_SESSION_HELPER_ID = "loyalty-points-per-session-helper";

const normalizeEarnMode = (value: unknown): LoyaltyEarnMode =>
  String(value || "").toUpperCase() === "SESSION" ? "SESSION" : "AMOUNT";

const pluralizePoints = (count: number) =>
  `${count} point${count === 1 ? "" : "s"}`;

type PointsFilterPreset =
  | "ALL"
  | "UNDER_100"
  | "50_100"
  | "OVER_100"
  | "CUSTOM";

// Points are whole numbers and the API's bounds are inclusive, so "under 100"
// stops at 99 and "over 100" starts at 101 — exactly 100 is only in 50–100.
const POINTS_FILTER_PRESETS: Record<
  Exclude<PointsFilterPreset, "CUSTOM">,
  LoyaltyUsersFilter
> = {
  ALL: {},
  UNDER_100: { maxPoints: 99 },
  "50_100": { minPoints: 50, maxPoints: 100 },
  OVER_100: { minPoints: 101 },
};

const POINTS_FILTER_OPTIONS: { label: string; value: PointsFilterPreset }[] = [
  { label: "All clients", value: "ALL" },
  { label: "Under 100", value: "UNDER_100" },
  { label: "50 – 100", value: "50_100" },
  { label: "Over 100", value: "OVER_100" },
  { label: "Custom range", value: "CUSTOM" },
];

const POINTS_BOUND_INPUT_PROPS = {
  inputMode: "numeric" as const,
  pattern: "[0-9]*",
  maxLength: 7,
};

const sanitizePointsBoundInput = (value: string) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, POINTS_BOUND_INPUT_PROPS.maxLength);

const readPointsBound = (value: string) =>
  value === "" ? undefined : Number(value);

const describePointsFilter = ({ minPoints, maxPoints }: LoyaltyUsersFilter) => {
  if (minPoints !== undefined && maxPoints !== undefined) {
    return `Clients with ${minPoints} to ${maxPoints} points`;
  }
  if (minPoints !== undefined) return `Clients with ${minPoints} points or more`;
  if (maxPoints !== undefined) return `Clients with ${maxPoints} points or fewer`;
  return "";
};

type SavedEarnSettings = {
  earnMode: LoyaltyEarnMode;
  /** null when no valid rate has been saved yet, so nothing is sent. */
  earn: { usd: number; points: number } | null;
  pointsPerSession: number;
};

type BusinessShareRow = {
  isFixedShare: boolean;
  /** Empty means "inherit the organization's number". */
  divisor: string;
};

const uniqueRewardOptions = (options: RewardItemOption[]) => {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (!option.value || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const uniqueRewardOptionsByLabel = (options: RewardItemOption[]) => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = option.label.trim().toLowerCase();
    if (!option.value || !key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeArrayResponse = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.businesses)) return response.businesses;
  if (Array.isArray(response?.data?.businesses))
    return response.data.businesses;
  if (Array.isArray(response?.services)) return response.services;
  if (Array.isArray(response?.data?.services)) return response.data.services;
  return [];
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const REWARD_TYPE_LABELS: Record<LoyaltyRewardType, string> = {
  PRODUCT: "Product",
  SERVICE: "Service",
};

// The API can hand a boolean back as "false" or 0, and both are truthy under a
// bare Boolean(), so read the flag rather than the box it arrived in.
const isRewardPinned = (reward: { pin?: boolean | string | number }) => {
  const value = reward?.pin;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return Boolean(value);
};

const normalizeRewardType = (value: unknown): LoyaltyRewardType =>
  String(value || "").toUpperCase() === "SERVICE" ? "SERVICE" : "PRODUCT";

const SERVICE_SCOPE_OPTIONS: { label: string; value: LoyaltyServiceScope }[] = [
  { label: "Any service", value: "ALL" },
  { label: "Only the services I pick", value: "INCLUDE" },
  { label: "Every service except the ones I pick", value: "EXCLUDE" },
];

const toServiceIdList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((id) => String(id || "")).filter(Boolean);
  }
  // A stringified array — "a,b" — is what a non-custom-render field turns into.
  if (typeof value === "string") {
    return value
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeServiceScope = (value: unknown): LoyaltyServiceScope => {
  const scope = String(value || "").toUpperCase();
  return scope === "INCLUDE" || scope === "EXCLUDE" ? scope : "ALL";
};

const normalizeRedeemDiscountType = (value: unknown): RedeemDiscountType =>
  String(value || "").toUpperCase() === "FIXED" ? "FIXED" : "PERCENT";

const formatTransactionDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatTransactionPaymentReference = (
  value?: string | null,
  type?: string,
) => {
  const reference = String(value || "").trim();
  if (!reference) return "-";

  const invoiceReference = reference.match(/^invoice[-_:\s]*(.+)$/i);
  if (invoiceReference?.[1]) {
    const invoiceId = invoiceReference[1].replace(
      /[-_:\s]*reward[-_:\s]*.+$/i,
      "",
    );
    return `invoice-${invoiceId}`;
  }

  const paymentReferenceTypes = new Set([
    "EARN",
    "SHARE_EARN",
    "REFUND",
    "SHARE_REFUND",
    "REWARD_REFUND",
  ]);

  if (paymentReferenceTypes.has(String(type || "").toUpperCase())) {
    return `payment-${reference}`;
  }

  return reference;
};

const setCachedSharePointsEnabled = (
  organizationId: string,
  value: boolean,
) => {
  if (!organizationId) return;
  localStorage.setItem(`loyalty_share_points_${organizationId}`, String(value));
};

const setCachedFullInvoiceEnabled = (
  organizationId: string,
  value: boolean,
) => {
  if (!organizationId) return;
  localStorage.setItem(`loyalty_full_invoice_${organizationId}`, String(value));
};

// The booking screen reads these to size the "Invite clients" picker before its
// own settings fetch resolves.
const setCachedFixedShare = (
  organizationId: string,
  enabled: boolean,
  divisor: number,
) => {
  if (!organizationId) return;
  localStorage.setItem(
    `loyalty_fixed_share_${organizationId}`,
    String(enabled),
  );
  localStorage.setItem(
    `loyalty_share_divisor_${organizationId}`,
    String(divisor),
  );
};

const setCachedBusinessShare = (
  organizationId: string,
  rows: BusinessShareSettingPayload[],
  orgDivisor: number,
) => {
  if (!organizationId) return;
  rows.forEach((row) => {
    localStorage.setItem(
      `loyalty_fixed_share_${organizationId}_${row.businessId}`,
      String(row.isFixedShare),
    );
    localStorage.setItem(
      `loyalty_share_divisor_${organizationId}_${row.businessId}`,
      String(row.shareDivisor ?? orgDivisor),
    );
  });
};

const setCachedLoyaltyEnabled = (organizationId: string, value: boolean) => {
  if (!organizationId) return;
  localStorage.setItem(`loyalty_enabled_${organizationId}`, String(value));
};

const getStoredOrganizationId = () => {
  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;
  return (
    user?.organizationId ||
    user?.organization_id ||
    user?.orgId ||
    user?.organization?.id ||
    ""
  );
};

const getCachedBoolean = (organizationId: string, key: string) => {
  if (!organizationId) return false;
  const directValue = localStorage.getItem(`${key}_${organizationId}`);
  if (directValue === "true") return true;
  if (directValue === "false") return false;

  const storedOrganizationId = getStoredOrganizationId();
  const storedValue = storedOrganizationId
    ? localStorage.getItem(`${key}_${storedOrganizationId}`)
    : null;
  return storedValue === "true";
};

const getCachedLoyaltyEnabled = (organizationId: string) =>
  getCachedBoolean(organizationId, "loyalty_enabled");

const getCachedSharePointsEnabled = (organizationId: string) =>
  getCachedBoolean(organizationId, "loyalty_share_points");

const getCachedFullInvoiceEnabled = (organizationId: string) =>
  getCachedBoolean(organizationId, "loyalty_full_invoice");

const cacheLoyaltyEnabled = (routeOrganizationId: string, value: boolean) => {
  setCachedLoyaltyEnabled(routeOrganizationId, value);
  const storedOrganizationId = getStoredOrganizationId();
  if (storedOrganizationId && storedOrganizationId !== routeOrganizationId) {
    setCachedLoyaltyEnabled(storedOrganizationId, value);
  }
};

const cacheSharePointsEnabled = (
  routeOrganizationId: string,
  value: boolean,
) => {
  setCachedSharePointsEnabled(routeOrganizationId, value);
  const storedOrganizationId = getStoredOrganizationId();
  if (storedOrganizationId && storedOrganizationId !== routeOrganizationId) {
    setCachedSharePointsEnabled(storedOrganizationId, value);
  }
  window.dispatchEvent(
    new CustomEvent("loyalty-share-points-changed", { detail: { value } }),
  );
};

const cacheFixedShare = (
  routeOrganizationId: string,
  enabled: boolean,
  divisor: number,
) => {
  setCachedFixedShare(routeOrganizationId, enabled, divisor);
  const storedOrganizationId = getStoredOrganizationId();
  if (storedOrganizationId && storedOrganizationId !== routeOrganizationId) {
    setCachedFixedShare(storedOrganizationId, enabled, divisor);
  }
};

const cacheBusinessShare = (
  routeOrganizationId: string,
  rows: BusinessShareSettingPayload[],
  orgDivisor: number,
) => {
  setCachedBusinessShare(routeOrganizationId, rows, orgDivisor);
  const storedOrganizationId = getStoredOrganizationId();
  if (storedOrganizationId && storedOrganizationId !== routeOrganizationId) {
    setCachedBusinessShare(storedOrganizationId, rows, orgDivisor);
  }
};

const cacheFullInvoiceEnabled = (
  routeOrganizationId: string,
  value: boolean,
) => {
  setCachedFullInvoiceEnabled(routeOrganizationId, value);
  const storedOrganizationId = getStoredOrganizationId();
  if (storedOrganizationId && storedOrganizationId !== routeOrganizationId) {
    setCachedFullInvoiceEnabled(storedOrganizationId, value);
  }
};

export type LoyaltySection = "settings" | "clients";

interface LoyaltyProps {
  /**
   * The page grew past what one screen should hold, so it is split in two:
   * "settings" keeps the program configuration, "clients" keeps point
   * transfers and the client balances table.
   */
  section?: LoyaltySection;
}

const Loyalty: React.FC<LoyaltyProps> = ({ section = "settings" }) => {
  const showSettings = section === "settings";
  const showClients = section === "clients";
  const params = useParams<{ businessId?: string; id?: string }>();
  const tenantId =
    getStoredOrganizationId() || params.businessId || params.id || "";

  const [usd, setUsd] = useState("");
  const [earnPoints, setEarnPoints] = useState("");
  const [earnMode, setEarnMode] = useState<LoyaltyEarnMode>("AMOUNT");
  const [pointsPerSession, setPointsPerSession] = useState("0");
  // The mode that is not selected is saved exactly as the server last held
  // it, so switching back restores that value rather than a hidden edit.
  const savedEarnSettings = useRef<SavedEarnSettings>({
    earnMode: "AMOUNT",
    earn: null,
    pointsPerSession: 0,
  });
  const [redeemPoints, setRedeemPoints] = useState("");
  const [discount, setDiscount] = useState("");
  const [redeemDiscountType, setRedeemDiscountType] =
    useState<RedeemDiscountType>("PERCENT");
  const [transferFromUserId, setTransferFromUserId] = useState("");
  const [transferToUserId, setTransferToUserId] = useState("");
  const [transferPoints, setTransferPoints] = useState("");
  const [adminLoyaltyEnabled, setAdminLoyaltyEnabled] = useState(() =>
    getCachedLoyaltyEnabled(tenantId),
  );
  const [sharePointsEnabled, setSharePointsEnabled] = useState(() =>
    getCachedSharePointsEnabled(tenantId),
  );
  const [fullInvoiceEnabled, setFullInvoiceEnabled] = useState(() =>
    getCachedFullInvoiceEnabled(tenantId),
  );
  const [fixedShareEnabled, setFixedShareEnabled] = useState(false);
  const [shareDivisor, setShareDivisor] = useState(
    String(FIXED_SHARE_DEFAULT_DIVISOR),
  );
  const [savedShareDivisor, setSavedShareDivisor] = useState(
    FIXED_SHARE_DEFAULT_DIVISOR,
  );
  const [search, setSearch] = useState("");
  const [pointsPreset, setPointsPreset] = useState<PointsFilterPreset>("ALL");
  const [customMinPoints, setCustomMinPoints] = useState("");
  const [customMaxPoints, setCustomMaxPoints] = useState("");
  const [appliedCustomRange, setAppliedCustomRange] =
    useState<LoyaltyUsersFilter>({});
  const [rows, setRows] = useState<LoyaltyUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingLoyalty, setUpdatingLoyalty] = useState(false);
  const [updatingSharePoints, setUpdatingSharePoints] = useState(false);
  const [updatingFullInvoice, setUpdatingFullInvoice] = useState(false);
  const [updatingFixedShare, setUpdatingFixedShare] = useState(false);
  const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
  const [businessNamesUnavailable, setBusinessNamesUnavailable] =
    useState(false);
  const [businessShareRows, setBusinessShareRows] = useState<
    Record<string, BusinessShareRow>
  >({});
  const [configuredBusinessIds, setConfiguredBusinessIds] = useState<string[]>(
    [],
  );
  const [savingBusinessId, setSavingBusinessId] = useState("");
  // Mirrors what the server holds, so an edited-but-unsaved input can be
  // compared against it and rolled back to it.
  const savedBusinessShareRows = useRef<Record<string, BusinessShareRow>>({});
  const [savingEarnSettings, setSavingEarnSettings] = useState(false);
  const [savingRedeemSettings, setSavingRedeemSettings] = useState(false);
  const [transferringPoints, setTransferringPoints] = useState(false);
  const [initializingExistingUsers, setInitializingExistingUsers] =
    useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<LoyaltyUserRow | null>(null);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionUser, setTransactionUser] = useState<LoyaltyUserRow | null>(
    null,
  );
  const [transactions, setTransactions] = useState<LoyaltyTransactionRow[]>([]);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [rewardSearch, setRewardSearch] = useState("");
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<LoyaltyReward | null>(
    null,
  );
  const [rewardToDelete, setRewardToDelete] = useState<LoyaltyReward | null>(
    null,
  );
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [savingReward, setSavingReward] = useState(false);
  const [pinningRewardId, setPinningRewardId] = useState<string | null>(null);
  const [rewardProducts, setRewardProducts] = useState<RewardItemOption[]>([]);
  const [rewardServices, setRewardServices] = useState<RewardItemOption[]>([]);
  const [serviceCatalogue, setServiceCatalogue] = useState<
    ServiceCatalogueEntry[]
  >([]);
  const [loadingServiceCatalogue, setLoadingServiceCatalogue] = useState(false);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  // Whether the admin actually edited the list. An untouched list is left out
  // of the save entirely, and the backend keeps what it already has.
  const [servicePickerTouched, setServicePickerTouched] = useState(false);

  const customRangeError = (() => {
    const min = readPointsBound(customMinPoints);
    const max = readPointsBound(customMaxPoints);
    return min !== undefined && max !== undefined && min > max
      ? "Min points can't be more than max points."
      : "";
  })();

  // Typing "150" would otherwise fetch for 1, 15 and 150; wait for a pause,
  // and never send a range the API would reject.
  useEffect(() => {
    if (pointsPreset !== "CUSTOM" || customRangeError) return;
    const timer = window.setTimeout(() => {
      const minPoints = readPointsBound(customMinPoints);
      const maxPoints = readPointsBound(customMaxPoints);
      setAppliedCustomRange((current) =>
        current.minPoints === minPoints && current.maxPoints === maxPoints
          ? current
          : {
              ...(minPoints === undefined ? {} : { minPoints }),
              ...(maxPoints === undefined ? {} : { maxPoints }),
            },
      );
    }, 400);
    return () => window.clearTimeout(timer);
  }, [customMaxPoints, customMinPoints, customRangeError, pointsPreset]);

  const pointsFilter =
    pointsPreset === "CUSTOM"
      ? appliedCustomRange
      : POINTS_FILTER_PRESETS[pointsPreset];
  const pointsFilterDescription = describePointsFilter(pointsFilter);

  // The table asks the server for one page at a time; bumping this key makes
  // it fetch the current page again after points change.
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const refreshRows = useCallback(() => {
    setUsersRefreshKey((key) => key + 1);
  }, []);
  const debouncedSearch = useDebouncedValue(search, 400);

  const fetchFunc = useCallback(
    async (params?: any) => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || LOYALTY_USERS_PAGE_SIZE;
      if (!tenantId) {
        return { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      }
      setLoading(true);
      try {
        const result = await getLoyaltyUsersPage(tenantId, {
          page,
          size,
          search: String(params?.search || ""),
          ...pointsFilter,
        });
        const content = result.content.map(toLoyaltyUserRow);
        // The row actions look rows up by id on the page being shown.
        setRows(content);
        return { ...result, content };
      } catch (error: any) {
        toast.error(getErrorMessage(error, "Failed to fetch loyalty users."));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [pointsFilter, tenantId],
  );

  // Transfer pickers search the server too, so they never need the full list.
  const fetchTransferOptions = useCallback(
    async (term: string) => {
      if (!tenantId) return [];
      const result = await getLoyaltyUsersPage(tenantId, {
        page: 0,
        size: 20,
        search: term,
      });
      return result.content.map((user) => {
        const row = toLoyaltyUserRow(user);
        return {
          label: `${row.name}${row.mobile ? ` · ${row.mobile}` : ""} (${row.points} pts)`,
          value: row.id,
        };
      });
    },
    [tenantId],
  );
  const transferFromOptions = useRemoteSearchOptions(fetchTransferOptions);
  const transferToOptions = useRemoteSearchOptions(fetchTransferOptions);

  const refreshRewards = useCallback(async () => {
    try {
      setLoadingRewards(true);
      const response = await getRewards();
      // The API returns rewards newest-first and does not float pinned ones, so
      // do it here. Array#sort is stable, which keeps that id order inside each
      // group.
      setRewards(
        Array.isArray(response)
          ? [...response].sort(
              (a, b) => Number(isRewardPinned(b)) - Number(isRewardPinned(a)),
            )
          : [],
      );
    } catch (error: any) {
      setRewards([]);
      toast.error(getErrorMessage(error, "Failed to fetch rewards."));
    } finally {
      setLoadingRewards(false);
    }
  }, []);

  const refreshRewardItems = useCallback(async () => {
    try {
      setLoadingServiceCatalogue(true);
      const [productsResponse, businessesResponse] = await Promise.allSettled([
        fetchProducts(),
        getAllBusinesses(),
      ]);

      if (productsResponse.status === "fulfilled") {
        const products = normalizeArrayResponse(productsResponse.value);
        setRewardProducts(
          uniqueRewardOptions(
            products
              .map((product: any) => ({
                label: String(product?.name || product?.title || product?.id),
                value: String(product?.id || ""),
              }))
              .filter((option) => option.value),
          ),
        );
      } else {
        setRewardProducts([]);
      }

      if (businessesResponse.status === "fulfilled") {
        const businesses = normalizeArrayResponse(businessesResponse.value);
        const businessEntries = businesses
          .map((business: any) => ({
            id: String(business?.id || business?._id || ""),
            name: String(
              business?.name || business?.title || business?.business_name || "",
            ).trim(),
          }))
          .filter((business: { id: string }) => Boolean(business.id));

        // Units are only needed to name each row: services are duplicated per
        // unit, so "60 min" alone does not identify anything.
        const [serviceResponses, unitResponses] = await Promise.all([
          Promise.allSettled(
            businessEntries.map((business: { id: string }) =>
              getAllServicesByBusiness(business.id),
            ),
          ),
          Promise.allSettled(
            businessEntries.map((business: { id: string }) =>
              getAllBranches(business.id),
            ),
          ),
        ]);

        const unitNames = new Map<string, string>();
        unitResponses.forEach((response) => {
          if (response.status !== "fulfilled") return;
          normalizeArrayResponse(response.value).forEach((unit: any) => {
            const unitId = String(unit?.id || unit?.unit_id || unit?._id || "");
            if (!unitId) return;
            unitNames.set(
              unitId,
              String(unit?.name || unit?.unit_name || "").trim(),
            );
          });
        });

        const catalogue: ServiceCatalogueEntry[] = [];
        serviceResponses.forEach((response, index) => {
          if (response.status !== "fulfilled") return;
          const business = businessEntries[index];
          normalizeArrayResponse(response.value).forEach((service: any) => {
            const id = String(service?.id || service?._id || "");
            if (!id) return;
            const unitId = String(service?.unit_id || service?.unitId || "");
            catalogue.push({
              id,
              name: String(service?.name || service?.title || id),
              duration: Number(service?.duration ?? 0) || 0,
              businessId: business.id,
              businessName: business.name || `Business ${business.id}`,
              unitId: unitId || business.id,
              unitName: unitNames.get(unitId) || "Unassigned",
            });
          });
        });
        setServiceCatalogue(catalogue);

        const services = serviceResponses.flatMap((response) =>
          response.status === "fulfilled"
            ? normalizeArrayResponse(response.value)
            : [],
        );
        setRewardServices(
          uniqueRewardOptionsByLabel(
            uniqueRewardOptions(
              services
                .map((service: any) => ({
                  label: String(service?.name || service?.title || service?.id),
                  value: String(service?.id || service?._id || ""),
                }))
                .filter((option) => option.value),
            ),
          ),
        );
      } else {
        setRewardServices([]);
        setServiceCatalogue([]);
      }
    } catch {
      setRewardProducts([]);
      setRewardServices([]);
      setServiceCatalogue([]);
    } finally {
      setLoadingServiceCatalogue(false);
    }
  }, []);

  useEffect(() => {
    refreshRewards();
    refreshRewardItems();
  }, [refreshRewardItems, refreshRewards]);

  useEffect(() => {
    let cancelled = false;

    const loadBusinesses = async () => {
      try {
        const response = await getAllBusinesses();
        if (cancelled) return;
        const list = normalizeArrayResponse(response)
          .map((business: any) => ({
            id: String(
              business?.id || business?._id || business?.business_id || "",
            ),
            name: String(
              business?.name ||
                business?.title ||
                business?.business_name ||
                business?.short_name ||
                "",
            ).trim(),
          }))
          .filter((business: BusinessOption) => Boolean(business.id));
        setBusinesses(list);
        // Listing businesses needs its own permission. Without it we still know
        // the ids from the loyalty response, we just cannot name them.
        setBusinessNamesUnavailable(list.length === 0);
      } catch {
        if (cancelled) return;
        setBusinesses([]);
        setBusinessNamesUnavailable(true);
      }
    };

    loadBusinesses();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!tenantId) return;

      try {
        const settings = await getLoyaltySettings(tenantId);
        const loyaltyEnabled =
          typeof settings?.is_loyalty === "boolean"
            ? settings.is_loyalty
            : Boolean(settings?.isLoyalty);
        const fullInvoiceEnabled =
          typeof settings?.on_full_invoice === "boolean"
            ? settings.on_full_invoice
            : Boolean(settings?.onFullInvoice);
        const sharePointsEnabled =
          typeof settings?.is_share === "boolean"
            ? settings.is_share
            : Boolean(settings?.isShare);
        const fixedShareEnabledValue =
          typeof settings?.is_fixed_share === "boolean"
            ? settings.is_fixed_share
            : Boolean(settings?.isFixedShare);
        const shareDivisorValue = Number(
          settings?.share_divisor ?? settings?.shareDivisor,
        );
        const earnUsd =
          settings?.earn?.usd ?? settings?.earn_usd ?? settings?.earnUsd;
        const earnPointsValue =
          settings?.earn?.points ??
          settings?.earn_points ??
          settings?.earnPoints;
        const earnModeValue = normalizeEarnMode(
          settings?.earn_mode ?? settings?.earnMode,
        );
        const pointsPerSessionRaw = Number(
          settings?.earn_points_per_session ?? settings?.earnPointsPerSession,
        );
        const pointsPerSessionValue =
          Number.isInteger(pointsPerSessionRaw) && pointsPerSessionRaw >= 0
            ? pointsPerSessionRaw
            : 0;
        const redeemPointsValue =
          settings?.redeem?.points ??
          settings?.redeem_points ??
          settings?.redeemPoints;
        const redeemDiscountValue =
          settings?.redeem?.discount ??
          settings?.redeem_discount ??
          settings?.redeemDiscount;
        const redeemTypeValue = normalizeRedeemDiscountType(
          settings?.redeem?.type ?? settings?.type,
        );

        setAdminLoyaltyEnabled(loyaltyEnabled);
        cacheLoyaltyEnabled(tenantId, loyaltyEnabled);
        setSharePointsEnabled(sharePointsEnabled);
        cacheSharePointsEnabled(tenantId, sharePointsEnabled);
        setFullInvoiceEnabled(fullInvoiceEnabled);
        cacheFullInvoiceEnabled(tenantId, fullInvoiceEnabled);
        const businessRows = normalizeBusinessShareSettings(settings);
        const nextBusinessShareRows = businessRows.reduce<
          Record<string, BusinessShareRow>
        >((acc, row) => {
          acc[row.businessId] = {
            isFixedShare: row.isFixedShare,
            divisor: row.shareDivisor ? String(row.shareDivisor) : "",
          };
          return acc;
        }, {});
        setConfiguredBusinessIds(businessRows.map((row) => row.businessId));
        setBusinessShareRows(nextBusinessShareRows);
        savedBusinessShareRows.current = nextBusinessShareRows;

        setFixedShareEnabled(fixedShareEnabledValue);
        const nextDivisor =
          Number.isInteger(shareDivisorValue) &&
          shareDivisorValue >= FIXED_SHARE_MIN_DIVISOR
            ? shareDivisorValue
            : FIXED_SHARE_DEFAULT_DIVISOR;
        setShareDivisor(String(nextDivisor));
        setSavedShareDivisor(nextDivisor);
        cacheFixedShare(tenantId, fixedShareEnabledValue, nextDivisor);
        cacheBusinessShare(tenantId, businessRows, nextDivisor);
        if (earnUsd !== undefined && earnUsd !== null) {
          setUsd(String(earnUsd));
        }
        if (earnPointsValue !== undefined && earnPointsValue !== null) {
          setEarnPoints(String(earnPointsValue));
        }
        setEarnMode(earnModeValue);
        setPointsPerSession(String(pointsPerSessionValue));
        savedEarnSettings.current = {
          earnMode: earnModeValue,
          earn:
            Number(earnUsd) > 0 && Number(earnPointsValue) > 0
              ? { usd: Number(earnUsd), points: Number(earnPointsValue) }
              : null,
          pointsPerSession: pointsPerSessionValue,
        };
        if (redeemPointsValue !== undefined && redeemPointsValue !== null) {
          setRedeemPoints(String(redeemPointsValue));
        }
        if (redeemDiscountValue !== undefined && redeemDiscountValue !== null) {
          setDiscount(String(redeemDiscountValue));
        }
        setRedeemDiscountType(redeemTypeValue);
      } catch {
        setAdminLoyaltyEnabled(false);
        setSharePointsEnabled(false);
        setFullInvoiceEnabled(false);
        setFixedShareEnabled(false);
      }
    };

    fetchSettings();
  }, [tenantId]);

  const validatePositiveNumber = (value: string, label: string) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      toast.error(`${label} must be greater than 0.`);
      return null;
    }
    return numeric;
  };

  // Shared points are split and rounded down, so per-session points below the
  // largest possible split leave someone with 0. Derived from the live inputs
  // so it re-checks the moment sharing or any divisor changes.
  const sessionSplitDivisor = useMemo(
    () =>
      resolveSessionSplitDivisor(
        readDivisorInput(shareDivisor) ?? savedShareDivisor,
        Object.values(businessShareRows).map((row) => row?.divisor ?? ""),
      ),
    [businessShareRows, savedShareDivisor, shareDivisor],
  );

  const savedBusinessDivisors = (
    overrides: Record<string, string> = {},
  ) =>
    Object.values({
      ...Object.fromEntries(
        Object.entries(savedBusinessShareRows.current).map(([id, row]) => [
          id,
          row?.divisor ?? "",
        ]),
      ),
      ...overrides,
    });

  // The Earn card only guards its own Submit. Sharing and divisors save on
  // their own, so each of those saves re-checks the saved session points
  // against the N it is about to produce — otherwise players silently earn 0.
  const sessionPointsBlockSplit = (nextSplitDivisor: number) => {
    const saved = savedEarnSettings.current;
    if (
      saved.earnMode !== "SESSION" ||
      saved.pointsPerSession >= nextSplitDivisor
    ) {
      return false;
    }
    toast.error(
      `Points per session (${saved.pointsPerSession}) is lower than ${nextSplitDivisor} players, so each player would get 0 points. Raise points per session in Earn Points first.`,
    );
    return true;
  };

  const sessionPointsError = useMemo(() => {
    if (earnMode !== "SESSION") return null;
    const value = Number(pointsPerSession);
    if (pointsPerSession === "" || !Number.isInteger(value) || value < 1) {
      return "Points per session must be a whole number of at least 1.";
    }
    if (sharePointsEnabled && value < sessionSplitDivisor) {
      const n = sessionSplitDivisor;
      return `With point sharing on, each session's points are split between up to ${n} players and rounded down. Set at least ${n} points per session so every player gets at least 1 point.`;
    }
    return null;
  }, [earnMode, pointsPerSession, sessionSplitDivisor, sharePointsEnabled]);

  const sessionSplitHelper = useMemo(() => {
    if (earnMode !== "SESSION" || !sharePointsEnabled || sessionPointsError) {
      return null;
    }
    const value = Number(pointsPerSession);
    const perPlayer = Math.floor(value / sessionSplitDivisor);
    const dropped = value % sessionSplitDivisor;
    return [
      `Each player gets ${pluralizePoints(perPlayer)} per session.`,
      dropped > 0
        ? `(${pluralizePoints(dropped)} ${dropped === 1 ? "is" : "are"} dropped by rounding)`
        : "",
    ]
      .filter(Boolean)
      .join(" ");
  }, [
    earnMode,
    pointsPerSession,
    sessionPointsError,
    sessionSplitDivisor,
    sharePointsEnabled,
  ]);

  const handleSaveEarnSettings = async () => {
    if (!tenantId) return;

    let earn: SavedEarnSettings["earn"];
    let pointsPerSessionValue: number;

    if (earnMode === "SESSION") {
      if (sessionPointsError) return;
      pointsPerSessionValue = Number(pointsPerSession);
      earn = savedEarnSettings.current.earn;
    } else {
      const usdValue = validatePositiveNumber(usd, "USD");
      const pointsValue = validatePositiveNumber(earnPoints, "Points");

      if (usdValue === null || pointsValue === null) {
        return;
      }

      earn = { usd: usdValue, points: pointsValue };
      pointsPerSessionValue = savedEarnSettings.current.pointsPerSession;
    }

    try {
      setSavingEarnSettings(true);
      await saveLoyaltySettings({
        organizationId: tenantId,
        ...(earn ? { earn } : {}),
        earnMode,
        pointsPerSession: pointsPerSessionValue,
        isLoyalty: adminLoyaltyEnabled,
      });
      savedEarnSettings.current = {
        earnMode,
        earn,
        pointsPerSession: pointsPerSessionValue,
      };
      toast.success("Earn settings saved successfully!");
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to save earn settings."));
    } finally {
      setSavingEarnSettings(false);
    }
  };

  const handleSaveRedeemSettings = async () => {
    if (!tenantId) return;

    const pointsValue = validatePositiveNumber(redeemPoints, "Redeem points");
    const discountValue = validatePositiveNumber(discount, "Discount");

    if (pointsValue === null || discountValue === null) {
      return;
    }

    try {
      setSavingRedeemSettings(true);
      await saveLoyaltySettings({
        organizationId: tenantId,
        redeem: {
          points: pointsValue,
          discount: discountValue,
          type: redeemDiscountType,
        },
        isLoyalty: adminLoyaltyEnabled,
      });
      toast.success("Redeem settings saved successfully!");
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to save redeem settings."));
    } finally {
      setSavingRedeemSettings(false);
    }
  };

  const getRewardItemOptions = (rewardType: unknown) =>
    String(rewardType || "").toUpperCase() === "SERVICE"
      ? rewardServices
      : rewardProducts;

  const getRewardItemName = (reward: LoyaltyReward) => {
    const rewardType = normalizeRewardType(reward.reward_type);
    const options = rewardType === "PRODUCT" ? rewardProducts : rewardServices;
    return (
      options.find((option) => option.value === reward.reward_item_id)?.label ||
      reward.reward_item_id ||
      "-"
    );
  };

  const closeRewardDialog = () => {
    setRewardDialogOpen(false);
    setEditingReward(null);
    setServicePickerOpen(false);
    setServicePickerTouched(false);
  };

  /**
   * The API treats the three cases differently:
   *  - omitted        → the saved list is left alone
   *  - [] with ALL    → the list is cleared
   *  - [] with a scope → rejected
   * So an untouched list is omitted rather than echoed back, which keeps an
   * edit to the title alone from depending on the catalogue having loaded.
   */
  const buildServiceIdsPayload = (
    scope: LoyaltyServiceScope,
    serviceIds: string[],
  ) => {
    if (scope === "ALL") return { serviceIds: [] };
    const scopeChanged =
      !editingReward || scope !== getRewardServiceScope(editingReward);
    if (!servicePickerTouched && !scopeChanged) return {};
    return { serviceIds };
  };

  const handleSaveReward = async (values: Record<string, any>) => {
    const redeemPointsValue = Math.floor(Number(values.redeemPoints));
    const quantityValue = Math.floor(Number(values.quantity));
    const rewardType = normalizeRewardType(values.rewardType);
    const isServiceReward = rewardType === "SERVICE";
    const rewardItemId = String(values.rewardItemId || "");
    const serviceScope = isServiceReward
      ? normalizeServiceScope(values.serviceScope)
      : "ALL";
    const serviceIds = toServiceIdList(values.serviceIds);

    if (!Number.isFinite(redeemPointsValue) || redeemPointsValue <= 0) {
      toast.error("Redeem points must be greater than 0.");
      return;
    }

    if (!rewardItemId) {
      toast.error("Reward item is required.");
      return;
    }

    // "Include nothing" and "exclude nothing" are not meaningful states, and
    // the API rejects an empty list against either scope.
    if (serviceScope !== "ALL" && serviceIds.length === 0) {
      toast.error(
        `serviceScope ${serviceScope} needs at least one service. Pick one, or switch to Any service.`,
      );
      return;
    }

    if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
      toast.error("Quantity must be greater than 0.");
      return;
    }

    // Uniqueness is the backend's rule to enforce — guessing at it here only
    // blocked saves it would have accepted. Send it and surface the rejection.
    const payload = {
      title: String(values.title || "").trim(),
      description: String(values.description || "").trim(),
      image: values.image instanceof File ? values.image : undefined,
      imageUrl: String(values.imageUrl || "").trim() || undefined,
      redeemPoints: redeemPointsValue,
      rewardType,
      rewardItemId,
      // Scope only means anything for a service reward.
      ...(isServiceReward
        ? {
            serviceScope,
            ...buildServiceIdsPayload(serviceScope, serviceIds),
          }
        : {}),
      quantity: quantityValue,
    };

    try {
      setSavingReward(true);
      if (editingReward) {
        await updateReward(editingReward.id, payload);
        toast.success("Reward updated successfully!");
      } else {
        await createReward(payload);
        toast.success("Reward created successfully!");
      }
      closeRewardDialog();
      await refreshRewards();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to save reward."));
    } finally {
      setSavingReward(false);
    }
  };

  const openRewardEditForId = (id: string | number) => {
    const reward = rewards.find((item) => String(item.id) === String(id));
    if (!reward) return;
    setEditingReward(reward);
    setServicePickerTouched(false);
    setRewardDialogOpen(true);
  };

  const handleDeleteReward = async () => {
    if (!rewardToDelete) return;

    try {
      await deleteReward(rewardToDelete.id);
      toast.success("Reward deleted successfully!");
      if (editingReward?.id === rewardToDelete.id) {
        closeRewardDialog();
      }
      await refreshRewards();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to delete reward."));
    } finally {
      setRewardToDelete(null);
    }
  };

  const handleToggleRewardActive = async (reward: LoyaltyReward) => {
    try {
      await setRewardActive(reward.id, !reward.active);
      await refreshRewards();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to update reward status."));
    }
  };

  const handleToggleRewardPin = async (reward: LoyaltyReward) => {
    if (pinningRewardId) return;

    try {
      setPinningRewardId(String(reward.id));
      await setRewardPin(reward.id, !isRewardPinned(reward));
      await refreshRewards();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to update reward pin."));
    } finally {
      setPinningRewardId(null);
    }
  };

  const handleAdminLoyaltyChange = async (nextValue: boolean) => {
    if (!tenantId) return;

    const previousValue = adminLoyaltyEnabled;

    setAdminLoyaltyEnabled(nextValue);
    cacheLoyaltyEnabled(tenantId, nextValue);
    setUpdatingLoyalty(true);

    try {
      await updateLoyaltyEnabled({
        organizationId: tenantId,
        isLoyalty: nextValue,
      });
    } catch (error: any) {
      setAdminLoyaltyEnabled(previousValue);
      cacheLoyaltyEnabled(tenantId, previousValue);
      toast.error(getErrorMessage(error, "Failed to update loyalty status."));
    } finally {
      setUpdatingLoyalty(false);
    }
  };

  const handleFullInvoiceChange = async (nextValue: boolean) => {
    if (!tenantId) return;

    const previousValue = fullInvoiceEnabled;

    setFullInvoiceEnabled(nextValue);
    cacheFullInvoiceEnabled(tenantId, nextValue);
    setUpdatingFullInvoice(true);

    try {
      await updateFullInvoiceEnabled({
        organizationId: tenantId,
        onFullInvoice: nextValue,
      });
    } catch (error: any) {
      setFullInvoiceEnabled(previousValue);
      cacheFullInvoiceEnabled(tenantId, previousValue);
      toast.error(
        getErrorMessage(error, "Failed to update full invoice status."),
      );
    } finally {
      setUpdatingFullInvoice(false);
    }
  };

  const handleSharePointsChange = async (nextValue: boolean) => {
    if (!tenantId) return;

    if (
      nextValue &&
      sessionPointsBlockSplit(
        resolveSessionSplitDivisor(savedShareDivisor, savedBusinessDivisors()),
      )
    ) {
      return;
    }

    const previousValue = sharePointsEnabled;

    setSharePointsEnabled(nextValue);
    cacheSharePointsEnabled(tenantId, nextValue);
    setUpdatingSharePoints(true);

    try {
      await updateSharePointsEnabled({
        organizationId: tenantId,
        isShare: nextValue,
      });
    } catch (error: any) {
      setSharePointsEnabled(previousValue);
      cacheSharePointsEnabled(tenantId, previousValue);
      toast.error(
        getErrorMessage(error, "Failed to update share points status."),
      );
    } finally {
      setUpdatingSharePoints(false);
    }
  };

  const handleFixedShareChange = async (nextValue: boolean) => {
    if (!tenantId) return;

    const previousValue = fixedShareEnabled;
    const divisorValue = Number(shareDivisor);
    const nextDivisor =
      Number.isInteger(divisorValue) && divisorValue >= FIXED_SHARE_MIN_DIVISOR
        ? divisorValue
        : FIXED_SHARE_DEFAULT_DIVISOR;

    if (
      nextValue &&
      sharePointsEnabled &&
      sessionPointsBlockSplit(
        resolveSessionSplitDivisor(nextDivisor, savedBusinessDivisors()),
      )
    ) {
      return;
    }

    setFixedShareEnabled(nextValue);
    setUpdatingFixedShare(true);

    try {
      await updateFixedShareSettings({
        organizationId: tenantId,
        isFixedShare: nextValue,
        // Turning it on must carry a valid divisor — the backend rejects 0/1.
        ...(nextValue ? { shareDivisor: nextDivisor } : {}),
      });
      if (nextValue) {
        setShareDivisor(String(nextDivisor));
        setSavedShareDivisor(nextDivisor);
      }
      cacheFixedShare(tenantId, nextValue, nextDivisor);
    } catch (error: any) {
      setFixedShareEnabled(previousValue);
      toast.error(
        getErrorMessage(error, "Failed to update fixed share status."),
      );
    } finally {
      setUpdatingFixedShare(false);
    }
  };

  const handleShareDivisorCommit = async () => {
    if (!tenantId) return;

    const divisorValue = Number(shareDivisor);

    if (
      !Number.isInteger(divisorValue) ||
      divisorValue < FIXED_SHARE_MIN_DIVISOR ||
      divisorValue > FIXED_SHARE_MAX_DIVISOR
    ) {
      setShareDivisor(String(savedShareDivisor));
      toast.error(divisorRangeMessage());
      return;
    }

    if (divisorValue === savedShareDivisor) return;

    if (
      sharePointsEnabled &&
      sessionPointsBlockSplit(
        resolveSessionSplitDivisor(divisorValue, savedBusinessDivisors()),
      )
    ) {
      setShareDivisor(String(savedShareDivisor));
      return;
    }

    setUpdatingFixedShare(true);

    try {
      await updateFixedShareSettings({
        organizationId: tenantId,
        isFixedShare: true,
        shareDivisor: divisorValue,
      });
      setSavedShareDivisor(divisorValue);
      cacheFixedShare(tenantId, true, divisorValue);
      toast.success("Players per match saved.");
    } catch (error: any) {
      setShareDivisor(String(savedShareDivisor));
      toast.error(
        getErrorMessage(error, "Failed to update players per match."),
      );
    } finally {
      setUpdatingFixedShare(false);
    }
  };

  // Businesses the org actually has, plus any id the loyalty response knows
  // about — the latter keeps the list usable without the business-list
  // permission, just without names.
  const serviceCatalogueById = useMemo(
    () => new Map(serviceCatalogue.map((service) => [service.id, service])),
    [serviceCatalogue],
  );

  const describeService = useCallback(
    (serviceId: string) => {
      const service = serviceCatalogueById.get(serviceId);
      // Falls back to the raw id when the catalogue could not be loaded, so a
      // stale or unlisted id is still visible and removable.
      return service ? `${service.name} - ${service.unitName}` : serviceId;
    },
    [serviceCatalogueById],
  );

  const businessRowsToRender = useMemo(() => {
    const known = new Map<string, string>();
    businesses.forEach((business) => known.set(business.id, business.name));
    configuredBusinessIds.forEach((businessId) => {
      if (!known.has(businessId)) known.set(businessId, "");
    });
    return Array.from(known.entries()).map(([id, name]) => ({
      id,
      name: name || `Business ${id}`,
    }));
  }, [businesses, configuredBusinessIds]);

  const buildBusinessSharePayload = useCallback(
    (
      rows: Record<string, BusinessShareRow>,
      configuredIds: string[],
    ): BusinessShareSettingPayload[] =>
      Array.from(new Set(configuredIds)).map((businessId) => {
        const row = rows[businessId];
        const divisorValue = Number(row?.divisor);
        return {
          businessId,
          isFixedShare: Boolean(row?.isFixedShare),
          // Blank means inherit the organization's number.
          shareDivisor:
            row?.divisor && Number.isInteger(divisorValue)
              ? divisorValue
              : null,
        };
      }),
    [],
  );

  const persistBusinessShareSettings = useCallback(
    async (
      businessId: string,
      rows: Record<string, BusinessShareRow>,
      configuredIds: string[],
    ) => {
      if (!tenantId) return false;

      setSavingBusinessId(businessId);
      try {
        await saveBusinessShareSettings(
          tenantId,
          buildBusinessSharePayload(rows, configuredIds),
        );
        savedBusinessShareRows.current = rows;
        cacheBusinessShare(
          tenantId,
          buildBusinessSharePayload(rows, configuredIds),
          savedShareDivisor,
        );
        return true;
      } catch (error: any) {
        toast.error(
          getErrorMessage(error, "Failed to update this business's split."),
        );
        return false;
      } finally {
        setSavingBusinessId("");
      }
    },
    [buildBusinessSharePayload, savedShareDivisor, tenantId],
  );

  const handleBusinessFixedShareChange = async (
    businessId: string,
    nextValue: boolean,
  ) => {
    const previousRows = businessShareRows;
    const previousConfigured = configuredBusinessIds;
    const nextRows = {
      ...businessShareRows,
      [businessId]: {
        isFixedShare: nextValue,
        divisor: businessShareRows[businessId]?.divisor ?? "",
      },
    };
    const nextConfigured = configuredBusinessIds.includes(businessId)
      ? configuredBusinessIds
      : [...configuredBusinessIds, businessId];

    setBusinessShareRows(nextRows);
    setConfiguredBusinessIds(nextConfigured);

    const saved = await persistBusinessShareSettings(
      businessId,
      nextRows,
      nextConfigured,
    );

    if (!saved) {
      setBusinessShareRows(previousRows);
      setConfiguredBusinessIds(previousConfigured);
    }
  };

  const handleBusinessDivisorCommit = async (businessId: string) => {
    const savedDivisor = String(
      savedBusinessShareRows.current[businessId]?.divisor ?? "",
    );
    const rawValue = String(
      businessShareRows[businessId]?.divisor ?? "",
    ).trim();

    if (rawValue === savedDivisor) return;

    if (rawValue !== "") {
      const divisorValue = Number(rawValue);
      if (
        !Number.isInteger(divisorValue) ||
        divisorValue < FIXED_SHARE_MIN_DIVISOR ||
        divisorValue > FIXED_SHARE_MAX_DIVISOR
      ) {
        setBusinessShareRows((rows) => ({
          ...rows,
          [businessId]: {
            isFixedShare: Boolean(rows[businessId]?.isFixedShare),
            divisor: savedDivisor,
          },
        }));
        toast.error(divisorRangeMessage(true));
        return;
      }
    }

    if (
      sharePointsEnabled &&
      sessionPointsBlockSplit(
        resolveSessionSplitDivisor(
          savedShareDivisor,
          savedBusinessDivisors({ [businessId]: rawValue }),
        ),
      )
    ) {
      setBusinessShareRows((rows) => ({
        ...rows,
        [businessId]: {
          isFixedShare: Boolean(rows[businessId]?.isFixedShare),
          divisor: savedDivisor,
        },
      }));
      return;
    }

    const nextRows = {
      ...businessShareRows,
      [businessId]: {
        // A row the admin has not configured is showing the organization's
        // state, so typing a number here means "on, with this number".
        isFixedShare: configuredBusinessIds.includes(businessId)
          ? Boolean(businessShareRows[businessId]?.isFixedShare)
          : fixedShareEnabled,
        divisor: rawValue,
      },
    };
    const nextConfigured = configuredBusinessIds.includes(businessId)
      ? configuredBusinessIds
      : [...configuredBusinessIds, businessId];

    setBusinessShareRows(nextRows);
    setConfiguredBusinessIds(nextConfigured);

    const saved = await persistBusinessShareSettings(
      businessId,
      nextRows,
      nextConfigured,
    );

    if (saved) {
      toast.success("Business split saved.");
    } else {
      setBusinessShareRows((rows) => ({
        ...rows,
        [businessId]: {
          isFixedShare: Boolean(rows[businessId]?.isFixedShare),
          divisor: savedDivisor,
        },
      }));
    }
  };

  const handleTransferPoints = async () => {
    if (!tenantId) return;

    const fromUserId = transferFromUserId.trim();
    const toUserId = transferToUserId.trim();
    const pointsValue = Math.floor(Number(transferPoints));

    if (!fromUserId || !toUserId) {
      toast.error("From User ID and To User ID are required.");
      return;
    }

    if (fromUserId === toUserId) {
      toast.error("From User and To User must be different.");
      return;
    }

    if (!Number.isFinite(pointsValue) || pointsValue <= 0) {
      toast.error("Points must be greater than 0.");
      return;
    }

    try {
      setTransferringPoints(true);
      await transferLoyaltyPoints({
        organizationId: tenantId,
        fromUserId,
        toUserId,
        points: pointsValue,
      });
      toast.success("Points transferred successfully!");
      setTransferFromUserId("");
      setTransferToUserId("");
      setTransferPoints("");
      await refreshRows();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to transfer points."));
    } finally {
      setTransferringPoints(false);
    }
  };

  const handleInitializeExistingUsers = async () => {
    if (!tenantId) return;

    try {
      setInitializingExistingUsers(true);
      await initializeExistingLoyaltyUsers(tenantId);
      toast.success("Existing loyalty users initialized successfully!");
      await refreshRows();
    } catch (error: any) {
      toast.error(
        getErrorMessage(error, "Failed to initialize existing loyalty users."),
      );
    } finally {
      setInitializingExistingUsers(false);
    }
  };

  const openEditForId = (id: string | number) => {
    const found = rows.find((row) => String(row.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  const openTransactionsForId = async (id: string | number) => {
    if (!tenantId) return;

    const user = rows.find((row) => String(row.id) === String(id)) ?? null;
    setTransactionUser(user);
    setTransactions([]);
    setTransactionsOpen(true);
    setTransactionsLoading(true);

    try {
      const response = await getLoyaltyTransactions(tenantId, String(id));
      setTransactions(
        response.map((transaction: any, index: number) => ({
          id: transaction?.id ?? index,
          type: String(transaction?.type || "-").toUpperCase(),
          points: Number(transaction?.points) || 0,
          paymentId: transaction?.paymentId ?? transaction?.payment_id ?? null,
          createdAt: transaction?.createdAt ?? transaction?.created_at ?? null,
        })),
      );
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to fetch transactions."));
    } finally {
      setTransactionsLoading(false);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onViewHistory: { handler: openTransactionsForId },
  });

  const rewardActions = Actions({
    handleOpenEditModal: { handler: openRewardEditForId },
    onDelete: {
      handler: (id: string | number) => {
        const reward = rewards.find((item) => String(item.id) === String(id));
        if (reward) setRewardToDelete(reward);
      },
    },
  });

  const rewardFields: Field[] = useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        required: true,
        colSpan: 1,
        customRender: (value, onChange, _helpers, error) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomLabel
              text="Title"
              fontWeight={500}
              fontSize={14}
              highlightLabelStar
            />
            <CustomTextField
              label=""
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              error={Boolean(error)}
              helperText={error}
              fullWidth
            />
          </Box>
        ),
      },
      {
        name: "image",
        label: "Reward Image",
        colSpan: 1,
        customRender: (value, onChange) => {
          const savedImageUrl =
            editingReward?.image_url || editingReward?.imageUrl || "";

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomLabel text="Reward Image" fontWeight={500} fontSize={14} />
              <CustomTextField
                label=""
                type="file"
                inputProps={{ accept: "image/*" }}
                onChange={(event: any) => {
                  const file = event.target.files?.[0];
                  onChange(file || "");
                }}
                fullWidth
              />
              <CustomLabel
                text="Recommended: 1200 × 800 px (3:2), under ~250 KB."
                fontSize={12}
                color={COLORS.placeHolderColor}
                sx={{ lineHeight: 1.4 }}
              />
              {savedImageUrl && !value && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={savedImageUrl}
                    alt={editingReward?.title || "Reward"}
                    style={{ width: 160, height: 80, objectFit: "cover" }}
                  />
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: "description",
        label: "Description",
        colSpan: 1,
        customRender: (value, onChange) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomLabel text="Description" fontWeight={500} fontSize={14} />
            <CustomTextField
              label=""
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              fullWidth
            />
          </Box>
        ),
      },
      {
        name: "redeemPoints",
        label: "Redeem Points",
        type: "number",
        required: true,
        customRender: (value, onChange, _helpers, error) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomLabel
              text="Redeem Points"
              fontWeight={500}
              fontSize={14}
              highlightLabelStar
            />
            <CustomTextField
              label=""
              type="number"
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              error={Boolean(error)}
              helperText={error}
              fullWidth
            />
          </Box>
        ),
      },
      {
        name: "rewardType",
        label: "Reward Type",
        required: true,
        customRender: (value, _onChange, helpers) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomLabel
              text="Reward Type"
              fontWeight={500}
              fontSize={14}
              highlightLabelStar
            />
            <CustomSelect
              label=""
              value={value || "PRODUCT"}
              options={[
                { label: REWARD_TYPE_LABELS.PRODUCT, value: "PRODUCT" },
                { label: REWARD_TYPE_LABELS.SERVICE, value: "SERVICE" },
              ]}
              onChange={(nextValue) => {
                const normalizedType = normalizeRewardType(nextValue);
                helpers?.setFieldValue("rewardType", normalizedType);
                helpers?.setFieldValue("rewardItemId", "");
                if (normalizedType !== "SERVICE") {
                  // Scope is a service-reward idea only.
                  helpers?.setFieldValue("serviceScope", "ALL");
                  helpers?.setFieldValue("serviceIds", []);
                }
                if (normalizedType !== "PRODUCT") {
                  helpers?.setFieldValue("quantity", "1");
                }
              }}
              fullWidth
            />
          </Box>
        ),
      },
      {
        name: "rewardItemId",
        label: "Reward Item",
        required: true,
        customRender: (value, _onChange, helpers) => {
          const isServiceReward =
            normalizeRewardType(helpers?.values?.rewardType) === "SERVICE";

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomLabel
                text="Reward Item"
                fontWeight={500}
                fontSize={14}
                highlightLabelStar
              />
              <CustomSelect
                label=""
                value={value || ""}
                options={getRewardItemOptions(helpers?.values?.rewardType)}
                searchable
                placeholder="Select item"
                onChange={(nextValue) =>
                  helpers?.setFieldValue(
                    "rewardItemId",
                    Array.isArray(nextValue) ? "" : String(nextValue || ""),
                  )
                }
                fullWidth
              />
              {isServiceReward && (
                <CustomLabel
                  text="This service's duration is the free time. On a longer booking the client pays for the remainder."
                  fontSize={12}
                  color={COLORS.placeHolderColor}
                  sx={{ lineHeight: 1.4 }}
                />
              )}
            </Box>
          );
        },
      },
      {
        name: "serviceScope",
        label: "Redeemable against",
        colSpan: 2,
        customRender: (value, _onChange, helpers) => {
          if (normalizeRewardType(helpers?.values?.rewardType) !== "SERVICE") {
            return null;
          }

          const scope = normalizeServiceScope(value);
          const pickedIds = toServiceIdList(helpers?.values?.serviceIds);

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomLabel
                text="Redeemable against"
                fontWeight={500}
                fontSize={14}
              />
              <CustomSelect
                label=""
                value={scope}
                options={SERVICE_SCOPE_OPTIONS}
                onChange={(nextValue) => {
                  const nextScope = normalizeServiceScope(nextValue);
                  helpers?.setFieldValue("serviceScope", nextScope);
                  if (nextScope === "ALL") {
                    helpers?.setFieldValue("serviceIds", []);
                  }
                }}
                fullWidth
              />
              {scope !== "ALL" && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mt: 0.5,
                    }}>
                    <CustomButton
                      label={
                        pickedIds.length ? "Edit services" : "Select services"
                      }
                      variant="outlined"
                      size="small"
                      onClick={() => setServicePickerOpen(true)}
                    />
                    <CustomLabel
                      text={
                        scope === "INCLUDE"
                          ? "Only these services can use the reward."
                          : "Every service except these can use the reward."
                      }
                      fontSize={12}
                      color={COLORS.placeHolderColor}
                    />
                  </Box>

                  {pickedIds.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.5,
                      }}>
                      {pickedIds.map((serviceId) => (
                        <Chip
                          key={serviceId}
                          size="small"
                          label={describeService(serviceId)}
                          onDelete={() => {
                            setServicePickerTouched(true);
                            helpers?.setFieldValue(
                              "serviceIds",
                              pickedIds.filter((id) => id !== serviceId),
                            );
                          }}
                          sx={{ maxWidth: "100%" }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <CustomLabel
                      // Saving an empty list against INCLUDE/EXCLUDE is
                      // rejected — "include nothing" is not a real state.
                      text="Pick at least one service, or switch to Any service."
                      fontSize={12}
                      color={COLORS.ORANGE}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </>
              )}

              {/* Rendered here so it can write straight back into the form. */}
              <ServiceScopePickerDialog
                open={servicePickerOpen}
                onClose={() => setServicePickerOpen(false)}
                services={serviceCatalogue}
                selectedIds={pickedIds}
                loading={loadingServiceCatalogue}
                title={
                  scope === "INCLUDE"
                    ? "Services this reward covers"
                    : "Services this reward cannot be used on"
                }
                description="Services are duplicated per court, so each row is shown as service name - court name. Tick a business or court heading to take everything under it."
                onConfirm={(ids) => {
                  setServicePickerTouched(true);
                  helpers?.setFieldValue("serviceIds", ids);
                  setServicePickerOpen(false);
                }}
              />
            </Box>
          );
        },
      },
      {
        name: "serviceIds",
        label: "Services",
        colSpan: 2,
        // Registered only so the dialog keeps the array intact — it stringifies
        // any field without a customRender. The UI lives in serviceScope above.
        customRender: () => null,
      },
      {
        name: "quantity",
        label: "Quantity",
        type: "number",
        required: true,
        customRender: (value, onChange, helpers, error) => {
          // Only products can be redeemed more than once per reward.
          const isSingleQuantity =
            normalizeRewardType(helpers?.values?.rewardType) !== "PRODUCT";

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomLabel
                text="Quantity"
                fontWeight={500}
                fontSize={14}
                highlightLabelStar
              />
              <CustomTextField
                label=""
                type="number"
                value={isSingleQuantity ? "1" : value || ""}
                onChange={(event) => onChange(event.target.value)}
                error={Boolean(error)}
                helperText={error}
                inputProps={{ min: 1, step: 1 }}
                disabled={isSingleQuantity}
                fullWidth
              />
            </Box>
          );
        },
      },
    ],
    [
      describeService,
      editingReward,
      loadingServiceCatalogue,
      rewardProducts,
      rewardServices,
      serviceCatalogue,
      servicePickerOpen,
    ],
  );

  const fetchRewardsTable = useCallback(
    async (params?: any) => {
      const query = String(params?.search || "")
        .toLowerCase()
        .trim();
      const content = query
        ? rewards.filter((reward) =>
            [
              reward.reward_type,
              REWARD_TYPE_LABELS[normalizeRewardType(reward.reward_type)],
              getRewardItemName(reward),
              reward.reward_item_id,
              reward.redeem_points,
              reward.quantity,
              reward.active ? "active" : "inactive",
            ]
              .filter((value) => value !== undefined && value !== null)
              .join(" ")
              .toLowerCase()
              .includes(query),
          )
        : [...rewards];

      const pageSize = params?.size || 10;

      return {
        content,
        number: 0,
        size: pageSize,
        totalElements: content.length,
        totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
      };
    },
    [rewards, rewardProducts, rewardServices],
  );

  const editFields: Field[] = useMemo(
    () => [
      {
        name: "points",
        label: "Points",
        type: "number",
        required: true,
        colSpan: 2,
      },
    ],
    [],
  );


  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title={showSettings ? "Loyalty settings" : "Clients loyalty"}
          subtitle={
            showSettings
              ? "How clients earn and spend points."
              : "Points balance and history for each client."
          }
        />

        {showSettings && (<>
        <SettingsCard
          title="Program settings"
          description="How points are earned and split for this organization."
        >
          <SettingToggleRow
            title="Loyalty program"
            description="Turn the points program on or off. While it is off, clients stop earning points and rewards cannot be redeemed."
            checked={adminLoyaltyEnabled}
            disabled={updatingLoyalty || !tenantId}
            saving={updatingLoyalty}
            onChange={handleAdminLoyaltyChange}
          />

          <SettingToggleRow
            title="Share points"
            description="Split the points earned on an appointment between the clients attached to it, instead of giving the whole amount to the payer. Those clients are the ones added under Invite clients when you book."
            checked={sharePointsEnabled}
            disabled={updatingSharePoints || !tenantId}
            saving={updatingSharePoints}
            onChange={handleSharePointsChange}
          />

          <SettingToggleRow
            nested
            title="Always divide points by a fixed number (master switch)"
            description="Controls dividing only. Points are split by a set headcount instead of by who actually turned up: the payer banks their own share right away and the rest sit in a pool — anyone who accepts an invitation within 7 days claims their share, and whatever is left over expires. With this off nothing fixed-divides anywhere, whatever the businesses below say, but their Players per match still caps every booking."
            hint={
              sharePointsEnabled
                ? undefined
                : "Dividing takes effect once Share points is on. Players per match still applies."
            }
            checked={fixedShareEnabled}
            disabled={updatingFixedShare || !tenantId || !sharePointsEnabled}
            saving={updatingFixedShare}
            onChange={handleFixedShareChange}
          >
            <Box sx={{ maxWidth: 260 }}>
              <CustomLabel
                text="Players per match"
                fontWeight={500}
                fontSize={13}
                sx={{ mb: 0.5 }}
              />
              <CustomTextField
                label=""
                type="text"
                value={shareDivisor}
                onChange={(event) =>
                  setShareDivisor(sanitizeDivisorInput(event.target.value))
                }
                onBlur={handleShareDivisorCommit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    (event.target as HTMLInputElement).blur();
                  }
                }}
                inputProps={DIVISOR_INPUT_PROPS}
                disabled={updatingFixedShare || !tenantId}
                helperText={`A booking holds ${savedShareDivisor} players — the payer plus ${savedShareDivisor - 1} invited clients — whether or not points are being divided. ${FIXED_SHARE_MIN_DIVISOR}–${FIXED_SHARE_MAX_DIVISOR}, used by every business without its own number below.`}
                fullWidth
              />
            </Box>
          </SettingToggleRow>

          <SettingToggleRow
            title="Earn and redeem points on the full invoice, not just the service"
            description="On: the whole invoice earns points and a redeemed discount comes off all of it — products on the same invoice get discounted too. Off: only the service line earns, and the discount lands on that line alone. On a $40 court plus $50 of drinks, a 25% redemption saves the client $22.50 with this on versus $10.00 with it off. The value is stamped onto the invoice at booking time, so earlier invoices keep their old behaviour, and a Free time reward always discounts the service line whatever this says."
            checked={fullInvoiceEnabled}
            disabled={updatingFullInvoice || !tenantId}
            saving={updatingFullInvoice}
            onChange={handleFullInvoiceChange}
          />
        </SettingsCard>

        <SettingsCard
          title="Fixed split per business"
          description={
            !sharePointsEnabled
              ? "A padel court seats 4 and a football pitch 12. Each business's number caps its bookings whether or not points are shared — Share points above is off, so nothing is being split right now."
              : !fixedShareEnabled
                ? "A padel court seats 4 and a football pitch 12. Each business's number caps its bookings whether or not points are divided — the master switch above is off, so points still split between whoever is on the booking."
                : "A padel court seats 4 and a football pitch 12, so each business carries its own headcount. A business you leave alone inherits the organization's setting above."
          }
        >
          {businessRowsToRender.length === 0 ? (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
              <CustomLabel
                text="No businesses to configure yet."
                fontSize={14}
                color={COLORS.placeHolderColor}
              />
            </Box>
          ) : (
            businessRowsToRender.map((business) => {
              const row = businessShareRows[business.id];
              const isFixed = Boolean(row?.isFixedShare);
              const ownDivisor = String(row?.divisor || "");
              const isConfigured = configuredBusinessIds.includes(business.id);
              // An unconfigured business follows the organization, so show that
              // state on the switch rather than an off switch and a description
              // saying it is on.
              const effectiveFixedShare = isConfigured
                ? isFixed
                : fixedShareEnabled;
              // Roster size and the points split are separate: the number caps
              // the booking either way, the toggle only decides the split.
              const rosterNumber = Number(ownDivisor) || savedShareDivisor;
              const splitIsLive = sharePointsEnabled && fixedShareEnabled;
              const description = [
                `${rosterNumber} players per match — the payer plus ${rosterNumber - 1} guests.`,
                effectiveFixedShare && splitIsLive
                  ? `Points split ${rosterNumber} ways.`
                  : "Points split between whoever is on the booking.",
                isConfigured ? "" : "Inheriting the organization.",
              ]
                .filter(Boolean)
                .join(" ");
              const dividingHint = !sharePointsEnabled
                ? "Dividing takes effect once Share points is on."
                : !fixedShareEnabled
                  ? "Dividing takes effect once the master switch above is on."
                  : undefined;

              return (
                <SettingToggleRow
                  key={business.id}
                  title={business.name}
                  description={description}
                  hint={dividingHint}
                  checked={effectiveFixedShare}
                  disabled={!tenantId || savingBusinessId === business.id}
                  saving={savingBusinessId === business.id}
                  onChange={(nextValue) =>
                    handleBusinessFixedShareChange(business.id, nextValue)
                  }
                >
                  {/* Capacity, so it stays available whatever the switches say. */}
                  <Box sx={{ maxWidth: 260 }}>
                    <CustomLabel
                      text="Players per match"
                      fontWeight={500}
                      fontSize={13}
                      sx={{ mb: 0.5 }}
                    />
                    <CustomTextField
                      label=""
                      type="text"
                      value={ownDivisor}
                      placeholder={`Inherit (${savedShareDivisor})`}
                      onChange={(event) =>
                        setBusinessShareRows((rows) => ({
                          ...rows,
                          [business.id]: {
                            isFixedShare: Boolean(
                              rows[business.id]?.isFixedShare,
                            ),
                            divisor: sanitizeDivisorInput(
                              event.target.value,
                            ),
                          },
                        }))
                      }
                      onBlur={() => handleBusinessDivisorCommit(business.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          (event.target as HTMLInputElement).blur();
                        }
                      }}
                      inputProps={DIVISOR_INPUT_PROPS}
                      disabled={savingBusinessId === business.id || !tenantId}
                      helperText={`Caps how many people can be added to a booking, whether or not points are divided. ${FIXED_SHARE_MIN_DIVISOR}–${FIXED_SHARE_MAX_DIVISOR}, or blank to inherit the organization's number.`}
                      fullWidth
                    />
                  </Box>
                </SettingToggleRow>
              );
            })
          )}
        </SettingsCard>
        {businessNamesUnavailable && businessRowsToRender.length > 0 && (
          <CustomLabel
            text="Business names could not be loaded, so these rows are listed by id."
            fontSize={12}
            color={COLORS.ORANGE}
            sx={{ mt: -2, mb: 3, ml: 0.5 }}
          />
        )}
        </>)}
        <>
          {showSettings && (
          <Box
            sx={{
              p: 3,
              mb: 3,
              boxShadow: 2,
              borderRadius: 2,
              bgcolor: COLORS.WhiteBackground,
            }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 3,
                }}>
                <Box>
                  <CustomLabel
                    text="Earn Points"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={earnMode}
                    onChange={(_, value) =>
                      value && setEarnMode(normalizeEarnMode(value))
                    }
                    disabled={savingEarnSettings}
                    sx={{ mb: earnMode === "SESSION" ? 1 : 2 }}
                  >
                    <ToggleButton
                      value="AMOUNT"
                      sx={{ textTransform: "none", px: 2 }}
                    >
                      Per amount spent
                    </ToggleButton>
                    <ToggleButton
                      value="SESSION"
                      sx={{ textTransform: "none", px: 2 }}
                    >
                      Per session
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {earnMode === "SESSION" && (
                    <CustomLabel
                      text="Every paid session earns these points. Packages and class enrollments earn for each session they include (a 7-session package earns 7x). Sessions paid with package credits or fully with points don't earn again."
                      fontSize={13}
                      color={COLORS.placeHolderColor}
                      sx={{ mb: 2, maxWidth: 720 }}
                    />
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
                      gap: 2,
                      alignItems: "end",
                    }}>
                    {earnMode === "AMOUNT" ? (
                      <>
                        <CustomTextField
                          label="USD"
                          type="number"
                          value={usd}
                          onChange={(event) => setUsd(event.target.value)}
                          fullWidth
                        />
                        <CustomTextField
                          label="Points"
                          type="number"
                          value={earnPoints}
                          onChange={(event) =>
                            setEarnPoints(event.target.value)
                          }
                          fullWidth
                        />
                      </>
                    ) : (
                      <CustomTextField
                        label="Points per session"
                        type="text"
                        value={pointsPerSession}
                        onChange={(event) =>
                          setPointsPerSession(
                            sanitizePointsPerSessionInput(event.target.value),
                          )
                        }
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          "aria-describedby": POINTS_PER_SESSION_HELPER_ID,
                        }}
                        error={Boolean(sessionPointsError)}
                        fullWidth
                      />
                    )}
                    <CustomButton
                      label="Submit"
                      variant="contained"
                      startIcon={<AddIcon />}
                      loading={savingEarnSettings}
                      disabled={Boolean(sessionPointsError)}
                      onClick={handleSaveEarnSettings}
                      sx={{
                        width: { xs: "100%", md: "auto" },
                        gridColumn: { md: 3 },
                      }}
                    />
                  </Box>
                  {/* Under the row rather than in the field, so a long message
                      doesn't push the Submit button out of line. */}
                  {(sessionPointsError || sessionSplitHelper) && (
                    <FormHelperText
                      id={POINTS_PER_SESSION_HELPER_ID}
                      error={Boolean(sessionPointsError)}
                      sx={{ mt: 0.5, maxWidth: 720 }}
                    >
                      {sessionPointsError || sessionSplitHelper}
                    </FormHelperText>
                  )}
                </Box>

                <Box>
                  <CustomLabel
                    text="Redeem Points"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr 1fr auto",
                      },
                      gap: 2,
                      alignItems: "end",
                    }}>
                    <CustomTextField
                      label="Redeem Points"
                      type="number"
                      value={redeemPoints}
                      onChange={(event) => setRedeemPoints(event.target.value)}
                      fullWidth
                    />
                    <CustomSelect
                      label="Type"
                      value={redeemDiscountType}
                      options={[
                        { label: "Percent", value: "PERCENT" },
                        { label: "Fixed", value: "FIXED" },
                      ]}
                      onChange={(value) =>
                        setRedeemDiscountType(
                          normalizeRedeemDiscountType(value),
                        )
                      }
                      disabled={savingRedeemSettings}
                      fullWidth
                    />
                    <CustomTextField
                      label={
                        redeemDiscountType === "FIXED"
                          ? "Cash Back"
                          : "Discount %"
                      }
                      type="number"
                      value={discount}
                      onChange={(event) => setDiscount(event.target.value)}
                      fullWidth
                    />
                    <CustomButton
                      label="Submit"
                      variant="contained"
                      startIcon={<AddIcon />}
                      loading={savingRedeemSettings}
                      onClick={handleSaveRedeemSettings}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    />
                  </Box>
                </Box>

                <Box>
                  <CustomLabel
                    text="Rewards"
                    fontWeight="bold"
                    sx={{ mb: 0.25 }}
                  />
                  <CustomLabel
                    text="A service reward's free time is the duration of the service it points at. Redeemed against a longer booking it discounts the line in proportion and the client pays for the rest — a 60-minute reward on a 2-hour court is 50% off. Unless it covers the whole booking, a promo code and redeemed points can still apply to the remainder."
                    fontSize={13}
                    color={COLORS.placeHolderColor}
                    sx={{ mb: 1, lineHeight: 1.45, maxWidth: 720 }}
                  />
                  <SearchHeaderContainer
                    search={rewardSearch}
                    setSearch={setRewardSearch}
                    buttonConfig={[
                      {
                        size: "small",
                        bgColor: COLORS.MainColor,
                        startIcon: <AddIcon />,
                        onClick: () => {
                          setEditingReward(null);
                          setServicePickerTouched(false);
                          setRewardDialogOpen(true);
                        },
                        textColor: COLORS.WhiteText,
                        minHeight: 36,
                        maxHeight: 36,
                        sx: {
                          width: { xs: "96%", sm: 36 },
                          minWidth: { sm: 36 },
                          marginRight: { xs: 1.5, sm: 0 },
                        },
                      },
                    ]}
                  />
                  <CustomTable<LoyaltyReward>
                    filters={{ page: 0, size: 10, search: rewardSearch }}
                    clientSidePagination
                    loading={loadingRewards}
                    fields={[
                      {
                        key: "image_url",
                        header: "Image",
                        render: (row) => {
                          const imageUrl = row.image_url || row.imageUrl;
                          return imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={row.title || "Reward"}
                              style={{
                                width: 120,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            "-"
                          );
                        },
                      },
                      {
                        key: "title",
                        header: "Title",
                        render: (row) => row.title || "-",
                      },
                      {
                        key: "description",
                        header: "Description",
                        render: (row) => row.description || "-",
                      },
                      {
                      key: "reward_type",
                      header: "Reward Type",
                      render: (row) =>
                        REWARD_TYPE_LABELS[
                          normalizeRewardType(row.reward_type)
                        ],
                    },
                    {
                        key: "reward_item_id",
                        header: "Reward Item",
                        render: (row) => getRewardItemName(row),
                      },
                      {
                        key: "service_scope",
                        header: "Redeemable against",
                        render: (row) => {
                          if (normalizeRewardType(row.reward_type) !== "SERVICE") {
                            return "-";
                          }
                          const scope = getRewardServiceScope(row);
                          if (scope === "ALL") return "Any service";
                          const count = getRewardServiceIds(row).length;
                          return `${scope === "INCLUDE" ? "Only" : "Except"} ${count} service${count === 1 ? "" : "s"}`;
                        },
                      },
                      {
                        key: "redeem_points",
                        header: "Redeem Points",
                      },
                      { key: "quantity", header: "Quantity" },
                      {
                        key: "active",
                        header: "Active",
                        render: (row) => (
                          <Switch
                            checked={Boolean(row.active)}
                            onChange={() => handleToggleRewardActive(row)}
                            size="small"
                          />
                        ),
                      },
                      {
                        key: "pin",
                        header: "Pinned",
                        render: (row) => {
                          const pinned = isRewardPinned(row);
                          const busy = pinningRewardId === String(row.id);

                          return (
                            <Tooltip
                              title={
                                pinned
                                  ? "Unpin from booking flow"
                                  : "Pin to booking flow"
                              }
                            >
                              {/* Span keeps the tooltip alive while disabled. */}
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={
                                    pinned ? "Unpin reward" : "Pin reward"
                                  }
                                  onClick={() => handleToggleRewardPin(row)}
                                  disabled={Boolean(pinningRewardId)}
                                  sx={{
                                    color: pinned
                                      ? COLORS.MainColor
                                      : COLORS.placeHolderColor,
                                    transition:
                                      "color 150ms ease, transform 150ms ease",
                                    "&:hover": {
                                      color: COLORS.MainColor,
                                      transform: "scale(1.12)",
                                    },
                                  }}
                                >
                                  {busy ? (
                                    <CircularProgress size={18} />
                                  ) : pinned ? (
                                    <PushPinIcon fontSize="small" />
                                  ) : (
                                    <PushPinOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        },
                      },
                    ]}
                    actions={rewardActions}
                    fetchFunc={fetchRewardsTable}
                  />
                  <GenericFormDialog
                    open={rewardDialogOpen}
                    onClose={closeRewardDialog}
                    title={editingReward ? "Edit Reward" : "Add Reward"}
                    fields={rewardFields}
                    initialValues={
                      editingReward
                        ? {
                            title: editingReward.title || "",
                            description: editingReward.description || "",
                            imageUrl:
                              editingReward.image_url ||
                              editingReward.imageUrl ||
                              "",
                            redeemPoints: String(
                              editingReward.redeem_points || "",
                            ),
                            rewardType: normalizeRewardType(
                              editingReward.reward_type,
                            ),
                            rewardItemId: editingReward.reward_item_id || "",
                            serviceScope: getRewardServiceScope(editingReward),
                            serviceIds: getRewardServiceIds(editingReward),
                            quantity: String(editingReward.quantity || 1),
                          }
                        : {
                            title: "",
                            description: "",
                            imageUrl: "",
                            redeemPoints: "",
                            rewardType: "PRODUCT",
                            rewardItemId: "",
                            serviceScope: "ALL",
                            serviceIds: [],
                            quantity: "1",
                          }
                    }
                    submitLabel={editingReward ? "Update" : "Create"}
                    isSubmitting={savingReward}
                    disableSubmitUntilValid
                    onSubmit={handleSaveReward}
                  />
                  <ConfirmDialog
                    open={Boolean(rewardToDelete)}
                    onClose={() => setRewardToDelete(null)}
                    title="Delete reward"
                    message="Are you sure you want to delete this reward?"
                    onConfirm={handleDeleteReward}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {showClients && (<>
            <Box
              sx={{
                p: 3,
                mb: 3,
                boxShadow: 2,
                borderRadius: 2,
                bgcolor: COLORS.WhiteBackground,
              }}>
              <CustomLabel
                text="Transfer points between clients"
                fontWeight="bold"
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr 1fr auto",
                  },
                  gap: 2,
                  alignItems: "end",
                }}>
                <Box>
                  <CustomLabel text="From" fontWeight={500} fontSize={14} />
                  <CustomSelect
                    label=""
                    value={transferFromUserId}
                    options={transferFromOptions.withSelected([
                      transferFromUserId,
                    ])}
                    searchValue={transferFromOptions.search}
                    onSearchValueChange={transferFromOptions.setSearch}
                    filterLocally={false}
                    onChange={(value) =>
                      setTransferFromUserId(String(value || ""))
                    }
                    searchable
                    searchPlaceholder="Search clients"
                    fullWidth
                  />
                </Box>
                <Box>
                  <CustomLabel text="To" fontWeight={500} fontSize={14} />
                  <CustomSelect
                    label=""
                    value={transferToUserId}
                    options={transferToOptions.withSelected([
                      transferToUserId,
                    ])}
                    searchValue={transferToOptions.search}
                    onSearchValueChange={transferToOptions.setSearch}
                    filterLocally={false}
                    onChange={(value) =>
                      setTransferToUserId(String(value || ""))
                    }
                    searchable
                    searchPlaceholder="Search clients"
                    fullWidth
                  />
                </Box>
                <CustomTextField
                  label="Points"
                  type="number"
                  value={transferPoints}
                  onChange={(event) => setTransferPoints(event.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                />
                <CustomButton
                  label="Submit"
                  variant="contained"
                  startIcon={<AddIcon />}
                  loading={transferringPoints}
                  onClick={handleTransferPoints}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                />
              </Box>
            </Box>

            <SearchHeaderContainer
              search={search}
              setSearch={setSearch}
              selectConfig={{
                label: "Points",
                options: POINTS_FILTER_OPTIONS,
                value: pointsPreset,
                size: "small",
                onChange: (value: any) =>
                  setPointsPreset(
                    POINTS_FILTER_OPTIONS.some(
                      (option) => option.value === value,
                    )
                      ? (value as PointsFilterPreset)
                      : "ALL",
                  ),
              }}
              extraFilters={
                pointsPreset === "CUSTOM" && (
                  <>
                    <Box sx={{ width: { xs: "100%", sm: 120 } }}>
                      <CustomTextField
                        minWidth="0px"
                        placeholder="Min points"
                        bgColor={COLORS.WhiteBackground}
                        value={customMinPoints}
                        inputProps={{
                          ...POINTS_BOUND_INPUT_PROPS,
                          "aria-label": "Minimum points",
                        }}
                        onChange={(event) =>
                          setCustomMinPoints(
                            sanitizePointsBoundInput(event.target.value),
                          )
                        }
                        error={Boolean(customRangeError)}
                      />
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: 120 } }}>
                      <CustomTextField
                        minWidth="0px"
                        placeholder="Max points"
                        bgColor={COLORS.WhiteBackground}
                        value={customMaxPoints}
                        inputProps={{
                          ...POINTS_BOUND_INPUT_PROPS,
                          "aria-label": "Maximum points",
                        }}
                        onChange={(event) =>
                          setCustomMaxPoints(
                            sanitizePointsBoundInput(event.target.value),
                          )
                        }
                        error={Boolean(customRangeError)}
                      />
                    </Box>
                  </>
                )
              }
              buttonConfig={[
                {
                  label: "Refresh",
                  size: "small",
                  variant: "outlined",
                  startIcon: <RefreshIcon />,
                  onClick: () => refreshRows(),
                  disabled: loading,
                  minHeight: 36,
                  sx: {
                    width: { xs: "100%", sm: "auto" },
                  },
                },
              ]}
            />

            {/* The range error lives here, not under the inputs, so the
                filter row keeps one height. */}
            {(customRangeError || pointsFilterDescription) && (
              <Typography
                variant="body2"
                color={customRangeError ? "error" : "text.secondary"}
                sx={{ px: { xs: 1, sm: 0 }, mb: 1 }}
              >
                {customRangeError ||
                  `${pointsFilterDescription} (both ends included)`}
              </Typography>
            )}

            <CustomTable<LoyaltyUserRow>
              filters={{
                page: 0,
                size: LOYALTY_USERS_PAGE_SIZE,
                search: debouncedSearch,
                minPoints: pointsFilter.minPoints,
                maxPoints: pointsFilter.maxPoints,
              }}
              refreshSignal={usersRefreshKey}
              fields={[
                { key: "name", header: "Client" },
                { key: "mobile", header: "Mobile" },
                { key: "email", header: "Email" },
                { key: "points", header: "Points" },
              ]}
              actions={wiredActions}
              fetchFunc={fetchFunc}
            />

            <GenericFormDialog
              open={editOpen}
              onClose={() => {
                setEditOpen(false);
                setEditingRow(null);
              }}
              title={`Edit Points${editingRow?.name ? ` - ${editingRow.name}` : ""}`}
              fields={editFields}
              initialValues={{
                points:
                  editingRow?.points !== undefined &&
                  editingRow?.points !== null
                    ? String(editingRow.points)
                    : "0",
              }}
              submitLabel="Save"
              onSubmit={async (values) => {
                if (!editingRow || !tenantId) return;

                const nextPoints = Number(values.points);
                if (!Number.isFinite(nextPoints) || nextPoints < 0) {
                  toast.error("Points must be 0 or greater.");
                  return;
                }

                const roundedNextPoints = Math.floor(nextPoints);
                const difference =
                  roundedNextPoints - Number(editingRow.points || 0);

                try {
                  if (difference > 0) {
                    await addLoyaltyPoints({
                      tenantId,
                      userId: editingRow.id,
                      points: difference,
                    });
                  } else if (difference < 0) {
                    await removeLoyaltyPoints({
                      tenantId,
                      userId: editingRow.id,
                      points: Math.abs(difference),
                    });
                  }

                  toast.success("User loyalty points updated successfully!");
                  setEditOpen(false);
                  setEditingRow(null);
                  await refreshRows();
                } catch (error: any) {
                  toast.error(
                    getErrorMessage(error, "Failed to update loyalty points."),
                  );
                }
              }}
            />

            <Dialog
              open={transactionsOpen}
              onClose={() => setTransactionsOpen(false)}
              fullWidth
              maxWidth="md">
              <DialogTitle>
                Transactions
                {transactionUser?.name ? ` - ${transactionUser.name}` : ""}
              </DialogTitle>
              <DialogContent dividers>
                {transactionsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : transactions.length === 0 ? (
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}>
                    No transactions found.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Points</TableCell>
                          <TableCell>Payment reference</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.map((transaction) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell>{transaction.type}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  transaction.points < 0
                                    ? "error.main"
                                    : "success.main",
                                fontWeight: 600,
                              }}>
                              {transaction.points > 0 ? "+" : ""}
                              {transaction.points}
                            </TableCell>
                            <TableCell>
                              {formatTransactionPaymentReference(
                                transaction.paymentId,
                                transaction.type,
                              )}
                            </TableCell>
                            <TableCell>
                              {formatTransactionDate(transaction.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <CustomButton
                  label="Close"
                  variant="outlined"
                  onClick={() => setTransactionsOpen(false)}
                />
              </DialogActions>
            </Dialog>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                mb: 1,
              }}>
              <CustomButton
                label="initialize-existing-users"
                variant="contained"
                loading={initializingExistingUsers}
                onClick={handleInitializeExistingUsers}
              />
            </Box>
          </>)}
        </>
      </Box>
    </Box>
  );
};

export default Loyalty;
